import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useMatches, useParams } from 'react-router';
import { useIntercom } from 'react-use-intercom';
import { useDisclosure } from '@mantine/hooks';

import { ACCOUNT_INVOICE_PATH, ACCOUNT_SETTINGS_PATH } from 'constants/path';
import { KNOWLEDGE_BASE_URL, MARKETING_STORE_URL } from 'constants/chownow';

import {
  ActionIcon,
  Box,
  Group,
  Menu,
  Text,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';

import { useAuth } from 'hooks/useAuth';
import { logAnalytics } from 'helpers/loggers';
import { isInternalAdminPage } from 'helpers/routeMatches';

import DialogModal from 'components/Modals/DialogModal/DialogModal';
import FeatureRequests from 'components/FeatureRequests/FeatureRequests';
import TabletStatus from 'components/TabletStatus/TabletStatus';

import { HeadquarterContext } from 'context/HeadquarterContext';

import { useScopes } from 'hooks/useScopes';

import { canSeeInvoices } from 'scopes/cubohUserMetaHelpers';

import NavGlobalMobile from './NavGlobalMobile';
import NavGlobalDesktop from './NavGlobalDesktop';

import styles from './NavGlobal.module.scss';
import {
  ChatIconSm,
  HelpCircleIconSm,
  ModeIconMd,
  NavMenuIconSm,
  PersonIconSm,
} from '@chownow/pantry/icons';
export default function NavGlobal({ toggleSideMenu }) {
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');
  const matches = useMatches();

  const { restaurantId } = useParams();

  const { shutdown } = useIntercom();
  const {
    isUserCubohOnly,
    logout,
    isInternalUser,
    isImpersonating,
    isUserChowNowOnly,
    isUserCubohAndChowNow,
    setHasLogoutError,
    hasLogoutError,
  } = useAuth();
  const { scopeDetails } = useScopes();

  const { location } = useContext(HeadquarterContext);

  const [dialogModalOpened, dialogModalHandlers] = useDisclosure(false);
  const [featureRequestOpened, featureRequestHandlers] = useDisclosure(false);

  const canDisplayAccountNav = !isInternalUser && !isUserChowNowOnly;
  const showResourceCenter = isInternalUser || isUserChowNowOnly || isUserCubohAndChowNow;
  const showTabletStatus = location && !!restaurantId && !isUserCubohOnly;

  const toggleColorScheme = () => {
    setColorScheme(computedColorScheme === 'dark' ? 'light' : 'dark');
  };

  const isGlobalInternalPage = isInternalAdminPage(matches);

  useEffect(() => {
    if (hasLogoutError) {
      dialogModalHandlers.open();
    }
  }, [hasLogoutError]);

  return (
    <Box
      className={styles.globalNav}
      h={{ sm: 'var(--dimension-xl2)', lg: 'var(--dimension-xl3)' }}
      px={{ sm: 'sm', md: 'md', lg: 'xl' }}
      top={
        isInternalUser || isImpersonating
          ? { sm: 'var(--dimension-xl2)', lg: 'var(--dimension-xl3)' }
          : 0
      }
    >
      <Group gap="0" hiddenFrom="md">
        <NavGlobalMobile />
      </Group>

      <Group gap="sm" align="center" visibleFrom="md">
        <NavGlobalDesktop />
      </Group>

      <Group gap="xs2">
        {showTabletStatus && <TabletStatus />}
        {showResourceCenter && (
          <ActionIcon id="PendoResourceCenter">
            <HelpCircleIconSm />
          </ActionIcon>
        )}
        {!isUserCubohOnly && (
          <Tooltip label="Chat with support" position="bottom-end" withArrow arrowOffset={12}>
            <ActionIcon onClick={() => window.webchat?.open()}>
              <ChatIconSm />
            </ActionIcon>
          </Tooltip>
        )}
        <Menu menuItemTabIndex={0} position="bottom-end">
          <Menu.Target>
            <ActionIcon>
              <PersonIconSm />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {canDisplayAccountNav && (
              <Menu.Item component={Link} to={ACCOUNT_SETTINGS_PATH} fw="var(--font-weight-medium)">
                Account Settings
              </Menu.Item>
            )}
            {isUserCubohOnly && (
              <>
                {canSeeInvoices(scopeDetails) && (
                  <Menu.Item
                    component={Link}
                    to={ACCOUNT_INVOICE_PATH}
                    fw="var(--font-weight-medium)"
                  >
                    Account Invoice
                  </Menu.Item>
                )}
                <Menu.Item
                  component={Link}
                  to={KNOWLEDGE_BASE_URL}
                  target="_blank"
                  fw="var(--font-weight-medium)"
                >
                  Knowledge Base
                </Menu.Item>
                <Menu.Item onClick={featureRequestHandlers.open} fw="var(--font-weight-medium)">
                  Feature Request
                </Menu.Item>
              </>
            )}
            <Menu.Item
              component="a"
              href={MARKETING_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              fw="var(--font-weight-medium)"
              onClick={() => logAnalytics({ eventName: 'Marketing Store Clicked' })}
            >
              Marketing Store
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                shutdown();
                logout();
              }}
              fw="var(--font-weight-medium)"
            >
              Log out
            </Menu.Item>
            <Menu.Divider m={0} pt={0} styles={{ divider: { borderBottomStyle: 'dashed' } }} />
            <Menu.Item leftSection={<ModeIconMd />} onClick={toggleColorScheme}>
              <Text variant="sublabel-sm">
                {`Switch to ${computedColorScheme === 'dark' ? 'Light' : 'Dark'} Mode`}
              </Text>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        {!isGlobalInternalPage && (
          <ActionIcon onClick={toggleSideMenu} hiddenFrom="md" size="md">
            <NavMenuIconSm />
          </ActionIcon>
        )}
      </Group>
      <DialogModal
        opened={dialogModalOpened}
        close={dialogModalHandlers.close}
        title="Something went wrong."
        message="Sorry, an error has occurred. Please try again."
        showCloseIcon={false}
        onClose={() => setHasLogoutError(false)}
      />
      <FeatureRequests opened={featureRequestOpened} close={featureRequestHandlers.close} />
    </Box>
  );
}

NavGlobal.propTypes = {
  toggleSideMenu: PropTypes.func,
};
